import { useEffect, useMemo, useState } from "react";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { toast } from "sonner";
import { AlertTriangle, CheckCircle, GraduationCap, Save } from "lucide-react";
import {
  coachRequiredPhrasesFor,
  type CoachPromptKind,
} from "@itspip/shared";
import { RequireAdmin } from "@/components/shared/require-admin";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Label } from "@/components/ui/label";
import { Skeleton } from "@/components/ui/skeleton";
import { Textarea } from "@/components/ui/textarea";
import { apiClient } from "@/lib/api-client";

const MAX_PROMPT_CHARS = 10_000;

type PromptKind = CoachPromptKind;

interface CoachPromptsForm {
  objective_prompt: string;
  kr_prompt: string;
}

function useCoachPrompts() {
  return useQuery({
    queryKey: ["admin", "coach-prompts"],
    queryFn: async () => {
      const { data, error } = await apiClient.GET("/v1/admin/coach-prompts");
      if (error) throw new Error(error.error.message);
      return data.data;
    },
  });
}

interface UpdateError {
  message: string;
  /** Which field the server flagged, if identifiable from the message. */
  field: "objective_prompt" | "kr_prompt" | null;
}

function parseUpdateError(raw: string): UpdateError {
  if (raw.includes("objective_prompt")) {
    return { message: raw, field: "objective_prompt" };
  }
  if (raw.includes("kr_prompt")) {
    return { message: raw, field: "kr_prompt" };
  }
  return { message: raw, field: null };
}

function useUpdateCoachPrompts() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async (body: CoachPromptsForm) => {
      const { data, error } = await apiClient.PUT("/v1/admin/coach-prompts", {
        body,
      });
      if (error) throw new Error(error.error.message);
      return data.data;
    },
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: ["admin", "coach-prompts"] });
    },
  });
}

export function AdminCoachPromptsPage() {
  return (
    <RequireAdmin>
      <AdminCoachPromptsContent />
    </RequireAdmin>
  );
}

function AdminCoachPromptsContent() {
  const { data, isLoading, error } = useCoachPrompts();
  const updateMutation = useUpdateCoachPrompts();

  const [form, setForm] = useState<CoachPromptsForm>({
    objective_prompt: "",
    kr_prompt: "",
  });
  const [loaded, setLoaded] = useState(false);
  const [serverError, setServerError] = useState<UpdateError | null>(null);

  useEffect(() => {
    if (data && !loaded) {
      setForm({
        objective_prompt: data.objective_prompt,
        kr_prompt: data.kr_prompt,
      });
      setLoaded(true);
    }
  }, [data, loaded]);

  const isDirty =
    loaded &&
    data !== undefined &&
    (form.objective_prompt !== data.objective_prompt ||
      form.kr_prompt !== data.kr_prompt);

  const objMissing = useMemo(
    () => findMissing(form.objective_prompt, "objective"),
    [form.objective_prompt],
  );
  const krMissing = useMemo(
    () => findMissing(form.kr_prompt, "kr"),
    [form.kr_prompt],
  );

  const objValid =
    form.objective_prompt.trim().length > 0 &&
    form.objective_prompt.length <= MAX_PROMPT_CHARS &&
    objMissing.length === 0;
  const krValid =
    form.kr_prompt.trim().length > 0 &&
    form.kr_prompt.length <= MAX_PROMPT_CHARS &&
    krMissing.length === 0;
  const canSave = isDirty && objValid && krValid && !updateMutation.isPending;

  const handleSave = () => {
    setServerError(null);
    updateMutation.mutate(form, {
      onSuccess: () => {
        toast.success("Coaching prompts saved");
      },
      onError: (err) => {
        const message =
          err instanceof Error ? err.message : "Failed to save coaching prompts";
        const parsed = parseUpdateError(message);
        setServerError(parsed);
        toast.error(message);
      },
    });
  };

  if (isLoading) {
    return (
      <div className="space-y-6">
        <Skeleton className="h-9 w-72" />
        <Card>
          <CardHeader>
            <Skeleton className="h-6 w-48" />
          </CardHeader>
          <CardContent className="space-y-3">
            <Skeleton className="h-64 w-full" />
          </CardContent>
        </Card>
        <Card>
          <CardHeader>
            <Skeleton className="h-6 w-48" />
          </CardHeader>
          <CardContent className="space-y-3">
            <Skeleton className="h-64 w-full" />
          </CardContent>
        </Card>
      </div>
    );
  }

  if (error) {
    return (
      <div className="space-y-6">
        <h1 className="font-heading text-3xl font-semibold">Coach Prompts</h1>
        <p className="text-sm text-destructive">
          Failed to load prompts: {error instanceof Error ? error.message : "Unknown error"}
        </p>
      </div>
    );
  }

  return (
    <div className="space-y-6 pb-24">
      <div className="flex items-center gap-3">
        <GraduationCap className="h-6 w-6 text-[hsl(var(--primary))]" />
        <div>
          <h1 className="font-heading text-3xl font-semibold">Coach Prompts</h1>
          <p className="mt-1 text-sm text-[hsl(var(--muted-foreground))]">
            Edit the system prompts used by the AI OKR Coach. Changes take effect
            for new coaching sessions within 5 minutes.
          </p>
        </div>
      </div>

      <PromptEditor
        label="Objective Coaching Prompt"
        description="Methodology used when coaching a draft Objective."
        value={form.objective_prompt}
        onChange={(v) => setForm((f) => ({ ...f, objective_prompt: v }))}
        kind="objective"
        missingPhrases={objMissing}
        serverError={
          serverError?.field === "objective_prompt" ? serverError.message : null
        }
      />

      <PromptEditor
        label="Key Result Coaching Prompt"
        description="Methodology used when coaching draft Key Results."
        value={form.kr_prompt}
        onChange={(v) => setForm((f) => ({ ...f, kr_prompt: v }))}
        kind="kr"
        missingPhrases={krMissing}
        serverError={
          serverError?.field === "kr_prompt" ? serverError.message : null
        }
      />

      <div className="fixed inset-x-0 bottom-0 border-t bg-background">
        <div className="mx-auto flex max-w-7xl items-center justify-end gap-3 px-6 py-4">
          {isDirty && (
            <span className="text-sm text-[hsl(var(--muted-foreground))]">
              Unsaved changes
            </span>
          )}
          <Button onClick={handleSave} disabled={!canSave}>
            <Save className="mr-2 h-4 w-4" />
            {updateMutation.isPending ? "Saving..." : "Save Prompts"}
          </Button>
        </div>
      </div>
    </div>
  );
}

function findMissing(prompt: string, kind: PromptKind): string[] {
  return coachRequiredPhrasesFor(kind).filter((phrase) => !prompt.includes(phrase));
}

interface PromptEditorProps {
  label: string;
  description: string;
  value: string;
  onChange: (next: string) => void;
  kind: PromptKind;
  missingPhrases: readonly string[];
  serverError: string | null;
}

function PromptEditor({
  label,
  description,
  value,
  onChange,
  kind,
  missingPhrases,
  serverError,
}: PromptEditorProps) {
  const tooLong = value.length > MAX_PROMPT_CHARS;
  const empty = value.trim().length === 0;
  const required = coachRequiredPhrasesFor(kind);

  return (
    <Card>
      <CardHeader>
        <CardTitle>{label}</CardTitle>
        <p className="text-sm text-[hsl(var(--muted-foreground))]">{description}</p>
      </CardHeader>
      <CardContent className="space-y-3">
        <div className="rounded-md border border-[hsl(var(--border))] bg-[hsl(var(--muted))] p-3 text-xs">
          <p className="font-semibold">Required sentinel phrases</p>
          <p className="mt-1 text-[hsl(var(--muted-foreground))]">
            Each phrase must appear literally somewhere in the prompt — the
            wizard parses trailing lines matching these tokens as readiness
            signals.
          </p>
          <ul className="mt-2 space-y-1">
            {required.map((phrase) => {
              const present = !missingPhrases.includes(phrase);
              return (
                <li
                  key={phrase}
                  className={
                    present
                      ? "flex items-center gap-2 text-emerald-700"
                      : "flex items-center gap-2 text-destructive"
                  }
                >
                  {present ? (
                    <CheckCircle className="h-3.5 w-3.5" />
                  ) : (
                    <AlertTriangle className="h-3.5 w-3.5" />
                  )}
                  <code className="font-mono text-[11px]">{phrase}</code>
                  {!present && <span>missing</span>}
                </li>
              );
            })}
          </ul>
        </div>

        <Label htmlFor={`prompt-${label}`} className="sr-only">
          {label}
        </Label>
        <Textarea
          id={`prompt-${label}`}
          rows={16}
          value={value}
          onChange={(e) => onChange(e.target.value)}
          className="font-mono text-sm"
        />

        {serverError && (
          <p
            role="alert"
            className="rounded-md border border-destructive/30 bg-destructive/10 p-2 text-xs text-destructive"
          >
            {serverError}
          </p>
        )}

        <div className="flex justify-between text-xs">
          <span
            className={
              empty || tooLong || missingPhrases.length > 0
                ? "text-destructive"
                : "text-[hsl(var(--muted-foreground))]"
            }
          >
            {empty
              ? "Prompt cannot be empty"
              : tooLong
                ? `Over ${MAX_PROMPT_CHARS.toLocaleString()} character limit`
                : missingPhrases.length > 0
                  ? `Missing required phrase${missingPhrases.length > 1 ? "s" : ""}: ${missingPhrases.join(", ")}`
                  : "\u00a0"}
          </span>
          <span className="text-[hsl(var(--muted-foreground))]">
            {value.length.toLocaleString()} / {MAX_PROMPT_CHARS.toLocaleString()}
          </span>
        </div>
      </CardContent>
    </Card>
  );
}
